/* Premier Courses — generic segmented tabs.
   Drives any [data-tabs] group (e.g. the CA/FL state segmented control on the
   pricing page). No persistence — the chosen tab is in-memory only.

   Markup contract (per group):
     <div data-tabs>
       <div role="tablist">
         <button role="tab" data-tab-trigger="CA" aria-selected="true"  aria-controls="..." id="...">…</button>
         <button role="tab" data-tab-trigger="FL" aria-selected="false" aria-controls="..." id="...">…</button>
       </div>
       <div role="tabpanel" data-tab-panel="CA" id="...">…</div>
       <div role="tabpanel" data-tab-panel="FL" id="..." hidden>…</div>
     </div>

   The trigger's data-tab-trigger value is matched against a panel's
   data-tab-panel value. The initially-selected trigger (aria-selected="true",
   or the first one) decides which panel is shown on load.

   Sync: groups sharing a data-tabs-sync="<scope>" attribute mirror each
   other — activating a tab in one group activates the same value in every
   other group with that scope (e.g. the pricing CA/FL toggle drives the FAQ
   CA/FL toggle and vice versa). */
(function () {
  'use strict';

  var SYNC_EVENT = 'pc:tabs-sync';

  function initGroup(root) {
    if (root.dataset.pcTabsInit === '1') return;
    root.dataset.pcTabsInit = '1';

    // Groups can nest (state > stage > role) — only own this group's direct
    // triggers/panels, not those belonging to a nested [data-tabs] group.
    function owned(selector) {
      return Array.prototype.slice
        .call(root.querySelectorAll(selector))
        .filter(function (el) {
          return el.parentElement.closest('[data-tabs]') === root;
        });
    }
    var triggers = owned('[data-tab-trigger]');
    var panels = owned('[data-tab-panel]');
    if (!triggers.length || !panels.length) return;

    var syncScope = root.getAttribute('data-tabs-sync');

    function activate(value, focusTrigger, silent) {
      triggers.forEach(function (t) {
        var on = t.getAttribute('data-tab-trigger') === value;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.setAttribute('tabindex', on ? '0' : '-1');
        if (on) { t.dataset.on = 'true'; } else { delete t.dataset.on; }
        if (on && focusTrigger) { t.focus(); }
      });
      panels.forEach(function (p) {
        var on = p.getAttribute('data-tab-panel') === value;
        if (on) { p.removeAttribute('hidden'); }
        else { p.setAttribute('hidden', ''); }
      });
      if (syncScope && !silent) {
        document.dispatchEvent(new CustomEvent(SYNC_EVENT, {
          detail: { scope: syncScope, value: value, source: root }
        }));
      }
    }

    if (syncScope) {
      document.addEventListener(SYNC_EVENT, function (e) {
        if (e.detail.source === root || e.detail.scope !== syncScope) return;
        var known = triggers.some(function (t) {
          return t.getAttribute('data-tab-trigger') === e.detail.value;
        });
        if (known) { activate(e.detail.value, false, true); }
      });
    }

    triggers.forEach(function (trigger, i) {
      trigger.addEventListener('click', function () {
        activate(trigger.getAttribute('data-tab-trigger'), false);
      });

      // Roving-tabindex arrow-key navigation across the tablist.
      trigger.addEventListener('keydown', function (e) {
        var key = e.key;
        var next = null;
        if (key === 'ArrowRight' || key === 'ArrowDown') {
          next = triggers[(i + 1) % triggers.length];
        } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
          next = triggers[(i - 1 + triggers.length) % triggers.length];
        } else if (key === 'Home') {
          next = triggers[0];
        } else if (key === 'End') {
          next = triggers[triggers.length - 1];
        } else {
          return;
        }
        if (next) {
          e.preventDefault();
          activate(next.getAttribute('data-tab-trigger'), true);
        }
      });
    });

    // Establish the initial state from the pre-marked selected trigger.
    // Silent: page load must not broadcast to synced groups.
    var initial =
      root.querySelector('[data-tab-trigger][aria-selected="true"]') ||
      triggers[0];
    activate(initial.getAttribute('data-tab-trigger'), false, true);

    // Deep link: a URL param preselects the matching tab. The param name is
    // data-tabs-param (e.g. "stage", "role"); state-synced groups default to
    // "state" for back-compat. Groups with custom tab meanings (no sync, no
    // param attr — e.g. General/Course Info) don't react to URL params.
    try {
      var paramName = root.getAttribute('data-tabs-param') ||
        (syncScope === 'state' ? 'state' : '');
      var pre = paramName
        ? (new URLSearchParams(window.location.search).get(paramName) || '').toUpperCase()
        : '';
      if (pre) {
        var known = triggers.some(function (t) {
          return t.getAttribute('data-tab-trigger') === pre;
        });
        if (known) { activate(pre, false, true); }
      }
    } catch (e) { /* no-op */ }
  }

  function initAll(scope) {
    var ctx = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.slice
      .call(ctx.querySelectorAll('[data-tabs]'))
      .forEach(initGroup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  // Re-init when re-rendered inside the Shopify theme editor.
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
